import { DEV } from 'cc/env';
import { Enum, Director, director, _decorator, Component, UITransform, NodeEventType } from 'cc';

const { ccclass, requireComponent, property, executeInEditMode } = _decorator;

export enum ResizeMode {
    /**
     * 超過最大值則縮小，否則維持原尺寸。
     */
    Shrink,

    /**
     * 依照最大限制縮放，尺寸盡可能接近最大值但不超過，無論原始尺寸為何。
     */
    FitToMax,
}

@ccclass('UITransformScaler')
@executeInEditMode
@requireComponent(UITransform)
export default class UITransformScaler extends Component {
    @property({ formerlySerializedAs: 'resizeMode' })
    private _resizeMode: ResizeMode = ResizeMode.Shrink;

    @property({ formerlySerializedAs: 'setWidth' })
    private _setWidth: boolean = true;

    @property({ formerlySerializedAs: 'setHeight' })
    private _setHeight: boolean = true;

    @property({ formerlySerializedAs: 'maxWidth' })
    private _maxWidth: number = 0;

    @property({ formerlySerializedAs: 'maxHeight' })
    private _maxHeight: number = 0;

    private _dirty = false;

    @property({
        type: Enum(ResizeMode),
        tooltip: DEV && 'Shrink: 超過最大值則縮小，否則維持原尺寸。\nFitToMax: 依照最大限制縮放，尺寸盡可能接近最大值但不超過，無論原始尺寸為何。',
    })
    public get resizeMode(): ResizeMode {
        return this._resizeMode;
    }
    public set resizeMode(value: ResizeMode) {
        if (this._resizeMode === value) {
            return;
        }
        this._resizeMode = value;
        this.onDirty();
    }

    @property({ displayName: DEV && '是否設置寬度' })
    public get setWidth(): boolean {
        return this._setWidth;
    }

    public set setWidth(value: boolean) {
        if (this._setWidth === value) {
            return;
        }
        this._setWidth = value;
        this.onDirty();
    }

    @property({ displayName: DEV && '是否設置高度' })
    public get setHeight(): boolean {
        return this._setHeight;
    }
    public set setHeight(value: boolean) {
        if (this._setHeight === value) {
            return;
        }
        this._setHeight = value;
        this.onDirty();
    }

    @property({
        displayName: DEV && '最大高度',
        visible(this: UITransformScaler) {
            return this._setHeight && (this._resizeMode === ResizeMode.Shrink || this._resizeMode === ResizeMode.FitToMax);
        },
    })
    public get maxHeight(): number {
        return this._maxHeight;
    }
    public set maxHeight(value: number) {
        if (this._maxHeight === value) {
            return;
        }
        this._maxHeight = value;
        this.onDirty();
    }

    @property({
        displayName: DEV && '最大寬度',
        visible(this: UITransformScaler) {
            return this._setWidth && (this._resizeMode === ResizeMode.Shrink || this._resizeMode === ResizeMode.FitToMax);
        },
    })
    public get maxWidth(): number {
        return this._maxWidth;
    }
    public set maxWidth(value: number) {
        if (this._maxWidth === value) {
            return;
        }
        this._maxWidth = value;
        this.onDirty();
    }

    public onEnable(): void {
        this.node.on(NodeEventType.SIZE_CHANGED, this.onDirty, this);
        director.on(Director.EVENT_BEFORE_DRAW, this._onResize, this);
        this._dirty = true;
    }

    public onDestroy(): void {
        this.node.off(NodeEventType.SIZE_CHANGED, this.onDirty, this);
        director.off(Director.EVENT_BEFORE_DRAW, this._onResize, this);
    }

    public onDirty() {
        this._dirty = true;
    }

    private _onResize() {
        if (!this._dirty) {
            return;
        }
        this._dirty = false;

        const { transformHeight, transformWidth } = this.node;
        const scaleRatio = this._calculateScaleRatio(transformWidth, transformHeight);

        if (this.node.getScale().equals3f(scaleRatio, scaleRatio, scaleRatio)) {
            return;
        }
        this.node.setScale(scaleRatio, scaleRatio, scaleRatio);
    }

    private _calculateScaleRatio(width: number, height: number): number {
        let scaleX: number;
        let scaleY: number;
        switch (this.resizeMode) {
            case ResizeMode.Shrink: {
                scaleX = this.setWidth ? this.maxWidth / width : 1;
                scaleY = this.setHeight ? this.maxHeight / height : 1;
                // 若任一方向超過最大值，縮小
                return Math.min(1, scaleX, scaleY);
            }
            case ResizeMode.FitToMax: {
                // 不論大或小，都縮放讓尺寸接近最大值但不超過 (可放大也可縮小)
                scaleX = this.setWidth ? this.maxWidth / width : 1;
                scaleY = this.setHeight ? this.maxHeight / height : 1;
                return Math.min(scaleX, scaleY);
            }
            default:
                return 1; // 維持原比例
        }
    }
}
